import manifest from "../../../locales/manifest.json";

export type LocaleTag = string;

export interface LocaleDefinition {
  tag: LocaleTag;
  prefix: string;
  aliases: string[];
}

export const DEFAULT_LOCALE = manifest.default_locale as LocaleTag;
export const LOCALES = manifest.locales as LocaleDefinition[];
export const LOCALE_PREFERENCE_KEY = "lifty:v1:locale";

const byPrefix = new Map(
  LOCALES.filter((locale) => locale.prefix).map((locale) => [locale.prefix, locale])
);

export function normalizeLocale(value: string | null | undefined): LocaleTag | null {
  if (!value) return null;
  const normalized = value.trim().replace(/_/g, "-").toLowerCase();
  for (const locale of LOCALES) {
    if (
      locale.tag.toLowerCase() === normalized ||
      locale.prefix === normalized ||
      locale.aliases.some((alias) => alias.replace(/_/g, "-").toLowerCase() === normalized)
    ) {
      return locale.tag;
    }
  }
  const language = normalized.split("-")[0];
  return (
    LOCALES.find((locale) =>
      locale.aliases.some((alias) => alias.replace(/_/g, "-").toLowerCase() === language)
    )?.tag ?? null
  );
}

export function localeFromPathname(pathname: string): LocaleTag | null {
  const first = pathname.split("/").filter(Boolean)[0]?.toLowerCase();
  return first ? byPrefix.get(first)?.tag ?? null : null;
}

export function stripLocalePrefix(pathname: string): string {
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const segments = normalized.split("/").filter(Boolean);
  if (segments.length && byPrefix.has(segments[0].toLowerCase())) segments.shift();
  return `/${segments.join("/")}` || "/";
}

export function prefixForLocale(locale: LocaleTag): string {
  return LOCALES.find((entry) => entry.tag === locale)?.prefix ?? "";
}

export function localizePathname(pathname: string, locale: LocaleTag): string {
  const bare = stripLocalePrefix(pathname);
  const prefix = prefixForLocale(locale);
  return prefix ? `/${prefix}${bare === "/" ? "" : bare}` : bare;
}

export function localizeHref(href: string, locale: LocaleTag): string {
  const [pathAndSearch, hash = ""] = href.split("#", 2);
  const queryIndex = pathAndSearch.indexOf("?");
  const pathname = queryIndex >= 0 ? pathAndSearch.slice(0, queryIndex) : pathAndSearch;
  const search = queryIndex >= 0 ? pathAndSearch.slice(queryIndex) : "";
  return `${localizePathname(pathname, locale)}${search}${hash ? `#${hash}` : ""}`;
}

export function localeForBareRoot(
  storage: Pick<Storage, "getItem"> | null | undefined,
  languages: readonly string[]
): LocaleTag {
  try {
    const saved = normalizeLocale(storage?.getItem(LOCALE_PREFERENCE_KEY));
    if (saved) return saved;
  } catch {
    // Storage can be unavailable in privacy modes.
  }
  for (const language of languages) {
    const locale = normalizeLocale(language);
    if (locale) return locale;
  }
  return DEFAULT_LOCALE;
}

export function writeLocalePreference(locale: LocaleTag, storage?: Storage | null): void {
  try {
    (storage ?? window.localStorage).setItem(LOCALE_PREFERENCE_KEY, locale);
  } catch {
    // URL locale still works when storage is unavailable.
  }
}
